import type { MapDocument } from "../shared/types";
import type {
  VenueMapRoutePoint,
  VenueMapRouteSegment,
} from "./venueMapRouting";

/** Only routing inputs cross the thread boundary. No logos, architecture, labels or source metadata. */
export type RoutingDocument = Pick<MapDocument, "geometry" | "locations">;
export interface RoutingWorkerRequest {
  id: number;
  document?: RoutingDocument;
  from: string;
  to: string;
}
export interface RoutingWorkerResponse {
  id: number;
  points: VenueMapRoutePoint[] | null;
  segments?: VenueMapRouteSegment[];
  failed?: true;
}
export type RoutingFailure =
  "routing_worker_unavailable" | "routing_failed" | "routing_timeout";
export type RoutingResult =
  | { status: "cancelled" }
  | { status: "unavailable"; failure?: RoutingFailure }
  | {
      status: "ready";
      points: VenueMapRoutePoint[];
      segments: VenueMapRouteSegment[];
    };

export function routingDocument(document: MapDocument): RoutingDocument {
  const { geometry, locations } = document;
  return {
    geometry: {
      width: geometry.width,
      height: geometry.height,
      floor: geometry.floor,
      routing: geometry.routing && {
        walkableAreas: geometry.routing.walkableAreas.map((area) => ({
          ...area,
          name: "",
        })),
        barriers: geometry.routing.barriers.map((barrier) => ({
          ...barrier,
          name: "",
        })),
        doors: geometry.routing.doors.map((door) => ({ ...door, name: "" })),
      },
      stands: geometry.stands.map(({ id, polygons, hidden }) => ({
        id,
        polygons,
        hidden,
        name: "",
        shortName: "",
        category: "unassigned",
        label: {
          x: 0,
          y: 0,
          rotation: 0,
          idFontSize: 0,
          idY: 0,
          nameFontSize: 0,
          nameLines: [],
        },
      })),
      entrances: [],
    },
    locations: locations.map(({ id, x, y, hidden }) => ({
      id,
      x,
      y,
      hidden,
      label: "",
    })),
  };
}
